export type SupplierManagementMembershipOrigin = 'AUTOMATIC_LEGAL_IDENTIFIER' | 'USER_CONFIRMED';

export interface SupplierConsolidationSupplierResult {
  readonly id: string;
  readonly displayName: string;
  readonly email: string | null;
  readonly groupId: string | null;
  readonly groupName: string | null;
  readonly membershipOrigin: SupplierManagementMembershipOrigin | null;
  readonly lastImportedAt: string;
}

export interface SupplierConsolidationGroupResult {
  readonly id: string;
  readonly displayName: string;
  readonly members: readonly SupplierConsolidationSupplierResult[];
}

export interface SupplierConsolidationSuggestionResult {
  readonly id: string;
  readonly supplierReferenceIds: readonly string[];
  readonly proposedName: string;
  readonly reasons: readonly ('SAME_NORMALIZED_NAME' | 'NAME_CONTAINS' | 'SHARED_BRAND_TOKEN')[];
}

export interface SupplierSuggestionExclusionResult {
  readonly id: string;
  readonly leftSupplierReferenceId: string;
  readonly leftSupplierName: string;
  readonly rightSupplierReferenceId: string;
  readonly rightSupplierName: string;
  readonly createdAt: string;
}

export interface SupplierConsolidationResult {
  readonly suppliers: readonly SupplierConsolidationSupplierResult[];
  readonly groups: readonly SupplierConsolidationGroupResult[];
  readonly suggestions: readonly SupplierConsolidationSuggestionResult[];
  readonly exclusions?: readonly SupplierSuggestionExclusionResult[];
}

function isSupplierConsolidationResult(value: unknown): value is SupplierConsolidationResult {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Partial<SupplierConsolidationResult>;
  return (
    Array.isArray(candidate.suppliers) &&
    Array.isArray(candidate.groups) &&
    Array.isArray(candidate.suggestions) &&
    (candidate.exclusions === undefined || Array.isArray(candidate.exclusions))
  );
}

async function request(
  method: 'GET' | 'POST' | 'DELETE',
  path: string,
  csrfToken?: string,
  body?: unknown,
): Promise<SupplierConsolidationResult> {
  const response = await fetch('/api/integrations/freebe/suppliers/consolidation' + path, {
    method,
    credentials: 'same-origin',
    headers: {
      ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
      ...(csrfToken === undefined ? {} : { 'X-CSRF-Token': csrfToken }),
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  if (!response.ok) throw new Error('Supplier consolidation request failed.');
  const result: unknown = await response.json();
  if (!isSupplierConsolidationResult(result)) {
    throw new Error('Supplier consolidation response is invalid.');
  }
  return { ...result, exclusions: result.exclusions ?? [] };
}

export const supplierConsolidationApi = {
  read(): Promise<SupplierConsolidationResult> {
    return request('GET', '');
  },
  consolidate(
    displayName: string,
    supplierReferenceIds: readonly string[],
    csrfToken: string,
  ): Promise<SupplierConsolidationResult> {
    return request('POST', '/groups', csrfToken, { displayName, supplierReferenceIds });
  },
  dismissSuggestion(
    supplierReferenceIds: readonly string[],
    csrfToken: string,
  ): Promise<SupplierConsolidationResult> {
    return request('POST', '/suggestion-exclusions', csrfToken, { supplierReferenceIds });
  },
  restoreSuggestion(exclusionId: string, csrfToken: string): Promise<SupplierConsolidationResult> {
    return request(
      'DELETE',
      '/suggestion-exclusions/' + encodeURIComponent(exclusionId),
      csrfToken,
    );
  },
  detach(
    groupId: string,
    supplierReferenceId: string,
    csrfToken: string,
  ): Promise<SupplierConsolidationResult> {
    return request(
      'DELETE',
      '/groups/' +
        encodeURIComponent(groupId) +
        '/members/' +
        encodeURIComponent(supplierReferenceId),
      csrfToken,
    );
  },
};
